import { Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const POLL_INTERVAL = 5_000; // 5s — cadence unique, partagée par tous les abonnés.

/**
 * L'UNIQUE setInterval de polling de l'app.
 *
 * ChatService et CallService avaient chacun leur propre boucle de 5s,
 * indépendante et non coordonnée — deux requêtes réseau redondantes toutes
 * les 5 secondes. Aucun des deux ne pouvait simplement piloter l'autre :
 * CallService poll globalement depuis le boot (monté dans
 * MainLayoutComponent), alors que ChatService ne démarre le sien qu'à
 * l'ouverture de l'écran Messagerie — les rattacher l'un à l'autre aurait
 * changé le comportement de l'un des deux. Suit la Règle 12 du dépôt : un
 * même mécanisme demandé par deux appelants devient un troisième
 * collaborateur partagé, jamais une dépendance de l'un vers l'autre.
 */
@Injectable({ providedIn: 'root' })
export class PollingService implements OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly listeners = new Set<() => void>();

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.listeners.clear();
  }

  /**
   * Abonne un callback au tick partagé. Démarre le timer au premier
   * abonné (jamais côté serveur — SSR n'a rien à poller), l'arrête quand
   * le dernier abonné se désinscrit. Retourne la fonction de désabonnement.
   */
  register(callback: () => void): () => void {
    this.listeners.add(callback);
    if (this.isBrowser && !this.timer) {
      this.timer = setInterval(() => this.tick(), POLL_INTERVAL);
    }

    return () => {
      this.listeners.delete(callback);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  private tick(): void {
    for (const callback of this.listeners) {
      try {
        callback();
      } catch {
        // Un abonné en échec ne doit pas empêcher les autres de tourner.
      }
    }
  }
}
